import { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  GoAButton,
  GoACheckbox,
  GoAFormItem,
  GoASpacer,
} from '@abgov/react-components';
import { removeAllValidationErrorNotifications } from '../../state/slices/notificationSlice';
import { updateCollision } from '../../state/slices/collisionSlice';
import { Collision } from '../../models/collision';
import styles from './onboarding.module.css';
import { useIsDesktop } from '../../utils/isDesktop';

interface TermsAndConditionsComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const TermsAndConditionsComponent = ({
  navigationConfiguration,
}: TermsAndConditionsComponentProps) => {
  const dispatch = useAppDispatch();
  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const termsAndConditionsCheckboxFormErrorText =
    'You must agree to the terms and conditions before proceeding.';

  const [termsAndConditionsCheckbox, setTermsAndConditionsCheckbox] = useState(
    stateCollision?.collision?.agreementToTermsAndConditions ?? false,
  );
  const [
    termsAndConditionsCheckboxFormError,
    setTermsAndConditionsCheckboxFormError,
  ] = useState<boolean>(false);

  const [nextPage, setNextPage] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 150);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    );

    setNextPage(pageConfig?.default ?? 'error');
  }, []);

  const handleCheckboxChange = (checked: boolean) => {
    setTermsAndConditionsCheckbox(checked);
    setTermsAndConditionsCheckboxFormError(false);
  };

  const validate = (): boolean => {
    let isValid = true;
    dispatch(removeAllValidationErrorNotifications());

    if (!termsAndConditionsCheckbox) {
      setTermsAndConditionsCheckboxFormError(true);
      isValid = false;
    }
    return isValid;
  };

  const save = () => {
    let updatedCollision: Collision = { ...stateCollision.collision };
    updatedCollision.agreementToTermsAndConditions = termsAndConditionsCheckbox;

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  return (
    <div>
      <div>
        <h2 style={{ marginTop: '-35px' }}>
          Government of Alberta Report My Collision Online Service Terms of Use
        </h2>
        <GoASpacer vSpacing={'s'} />
        <h4>Effective as of May 21, 2025</h4>
        <GoASpacer vSpacing={'s'} />
        You are required to accept the Terms of Use below before using the
        online Report My Collison online service provided by the Registrar of
        Motor Vehicle Services (the “Registrar”). If you choose not to accept
        the Terms of Use, you will be unable to access Report My Collision.
      </div>
      <ol>
        <li>
          Introduction
          <ol>
            <li>
              The Report My Collision (RMC) is the property of the Registrar of
              Motor Vehicle Services (the “Registrar”) and is accessed through
              your Alberta.ca account.
            </li>
            <li>
              The information in the RMC is confidential, proprietary, and to
              these Terms of Use.
            </li>
            <li>
              By using the RMC, you agree to these Terms of Use. If you do not
              accept them, you must not proceed.
            </li>
          </ol>
        </li>
        <li>
          Collection statement
          <ol>
            <li>
              The personal information collected through the RMC is for the
              purposes of:
              <ul className="goa-unordered-list">
                <li>
                  Assisting you in preparing and assembling collision report
                  details, supporting information, and media.
                </li>
                <li>
                  Facilitating review and approval of your collision report by
                  law enforcement.
                </li>
              </ul>
            </li>
            <li>
              This collection is for Traffic Safety Act (TSA) purposes. If you
              have questions about the collection of personal information,
              contact:{' '}
              <a href="mailto:safetysystemssupport@gov.ab.ca">
                safetysystemssupport@gov.ab.ca
              </a>
            </li>
          </ol>
        </li>
        <li>
          Governing Law and Legal Framework
          <ol>
            <li>
              These Terms of Use are governed by and interpreted in accordance
              with the laws in force in Alberta, and you agree and submit to the
              exclusive jurisdiction of the courts in Alberta.
            </li>
            <li>
              All personal information you provide is collected for TSA purposes
              and may be disclosed under the Access to Motor Vehicle Information
              Regulation (AMVIR).
            </li>
          </ol>
        </li>
        <li>
          Accountability
          <ol>
            <li>
              You must comply with all applicable laws, including the TSA and
              associated Regulations.
            </li>
            <li>
              You may only use the service through your own registered
              Government of Alberta ("GoA") Alberta.ca Account for personal use,
              in accordance with the Alberta.ca Account Terms of Use.
            </li>
            <li>
              You may not share your login credentials or permit any
              unauthorized person to access or use the RMC.
            </li>
            <li>
              You are responsible for ensuring that the information you provide
              is complete, accurate and current. Any false, inaccurate, or
              misleading information will be considered a breach of these Terms
              of Use and may result in:
              <ul className="goa-unordered-list">
                <li>Rejection of your submission,</li>
                <li>Termination of access to RMC,</li>
                <li>Possible legal or regulatory consequences.</li>
              </ul>
            </li>
            <li>
              You agree to be responsible for any unauthorized collection, use
              and disclosure of personal information that results from
              inappropriate access or use of RMC.
            </li>
            <li>
              Misuse includes, but is not limited to submitting, accessing,
              viewing, or altering records you should not have access to, or
              allowing unauthorized persons to do so.
            </li>
          </ol>
        </li>
        <li>
          Information Provided
          <ol>
            <li>
              This Online Service allows you to submit collision information
              required under the TSA and its Regulations. Any other use is
              strictly prohibited.
            </li>
            <li>
              The Registrar GoA does not automatically collect any specific
              personal information from you, such as your name, mailing address,
              phone or fax number, or email address. The personal or business
              information you do provide will be used for the following
              purposes:
              <ul className="goa-unordered-list">
                <li>To confirm your identity.</li>
                <li>To monitor any request, release, or use of information.</li>
                <li>To monitor any request, release, or use of information.</li>
              </ul>
            </li>
            <li>
              If you have questions about the collection of information, please
              contact the appropriate office of the Registrar:
              <ol className={styles.simplenumbering}>
                <li>
                  Service Alberta & Red Tape Reduction, Motor Vehicles: (780)
                  427-0024 
                </li>
                <li>
                  Transportation & Economic Corridors, Traffic Safety Services:{' '}
                  <a href="mailto:safetysystemssupport@gov.ab.ca">
                    safetysystemssupport@gov.ab.ca
                  </a>
                </li>
              </ol>
            </li>
          </ol>
        </li>
        <li>
          Information Provided to Law Enforcement
          <ol>
            <li>
              Any collision information you submit, including personal details
              and media uploads, may be disclosed to law enforcement agencies to
              facilitate their review, validation, or approval of the collision
              report.
            </li>
          </ol>
        </li>
        <li>
          No Warranties
          <ol>
            <li>
              This Online Service is provided “as is” without warranty of any
              kind, including warranty of fitness for a particular purpose. The
              Registrar does not warrant the accuracy or the completeness of any
              release of information, or that this Online Service will function
              without error, failure, or interruption.
            </li>
          </ol>
        </li>
        <li>
          Limitation of Liability and Indemnification
          <ol>
            <li>
              By using RMC service, you expressly waive and release the
              Registrar, its employees, and agents from any and all liability
              for any loss or damage of any kind, including direct, indirect,
              incidental, punitive, or consequential damages, arising from the
              use, misuse, or functioning of the CSRS.
            </li>
            <li>
              You agree to indemnify and hold harmless the Registrar, its
              affiliates, employees, agents, and licensors from any claims or
              demands made by third parties due to or arising out of anything
              you submit, transmit, or upload to the CSRS.
            </li>
          </ol>
        </li>
        <li>
          Data security
          <ol>
            <li>You must not disrupt RMC or its accessibility to others.</li>
            <li>
              You must take all reasonable precautions to ensure that any
              material you upload is free of viruses or malicious code..
            </li>
            <li>
              Data scraping, data mining, framing, mirroring, or deep linking of
              any content accessed through RMC is strictly prohibited.
            </li>
          </ol>
        </li>
        <li>
          Retention of Records
          <ol>
            <li>
              You are responsible for maintaining your own copies of all
              documents or other information submitted through RMC
            </li>
            <li>
              Documents or other information accessed through RMC may be
              archived or deleted without notice.
            </li>
          </ol>
        </li>
        <li>
          Termination
          <ol>
            <li>
              You may deactivate your Alberta.ca account at any time in
              accordance with the Alberta.ca Account Terms of Use. The Registrar
              may retain your RMC account information for security and integrity
              purposes.
            </li>
            <li>
              The Registrar may suspend or terminate your access to RMC at any
              time, without notice.
            </li>
            <li>
              Even after termination, these Terms of Use continue to apply to
              your prior use of RMC. The Registrar also reserves the right to
              discontinue RMC entirely without notice.
            </li>
          </ol>
        </li>
        <li>
          Amendments
          <ol>
            <li>
              The Registrar may amend these Terms of Use at any time. You will
              have the opportunity to review the amended Terms of Use and either
              accept the amended Terms of Use or stop using this Online Service.
              You acknowledge that if you decide to stop using this Online
              Service the Registrar continues to maintain the authority to
              monitor, audit, or investigate complaints related to any previous
              request, release, or use of information.
            </li>
          </ol>
        </li>
        <li>
          Linked websites
          <ol>
            <li>
              The Registrar provides these links only as a service and
              convenience and does not take any responsibility for the content
              at the third-party sites. A link to these sites does not imply an
              endorsement or recommendation of the products, services, or
              information found there.
              <br />
              <br />
              A third-party service provider or website may have a privacy
              policy different from that of the Registrar and may provide less
              security than this Online Service. The Registrar disclaims all
              liability about your use of third-party service provider or access
              to linked web sites.
              <br />
              <br />
              The Registrar reserves the right to regularly review and
              re-evaluate all links or service providers and reserves the right
              to terminate any link or service provider without prior
              notification.
            </li>
          </ol>
        </li>
        <li>
          Communications
          <ol>
            <li>
              The Registrar reserves the right to regularly review and
              re-evaluate all links or service providers and reserves the right
              to terminate any link or service provider without prior
              notification.
            </li>
          </ol>
        </li>
      </ol>
      <div
        className={`${styles.stickyFooter} ${isVisible ? styles.visible : ''}`}
      >
        <GoAFormItem
          label=""
          error={
            termsAndConditionsCheckboxFormError
              ? termsAndConditionsCheckboxFormErrorText
              : ''
          }
          mb="none"
        >
          <GoACheckbox
            name="termsCheckbox"
            checked={termsAndConditionsCheckbox}
            onChange={(_, clicked) => handleCheckboxChange(clicked)}
            error={termsAndConditionsCheckboxFormError}
          >
            I agree to the Terms and Conditions
          </GoACheckbox>
        </GoAFormItem>
        <GoAButton
          onClick={() => {
            if (!validate()) {
              return;
            }
            save();
            navigationConfiguration.navigate(nextPage);
          }}
          width="300px"
        >
          Check Eligibility
        </GoAButton>
      </div>
    </div>
  );
};
